import { AnyScale, ScaleValue } from '@dfnivo/scales'
import { CanvasAxisProps } from '../types'
import { CompleteTheme } from '@dfnivo/core'
import { positions } from '../props'
import { getFormatter } from '../compute'
import { getCanvasAxis } from './axis'
import { useMemo } from 'react'

export type GetCanvasAxesProps<X extends ScaleValue, Y extends ScaleValue> = {
    xScale: AnyScale
    yScale: AnyScale
    width: number
    height: number
    top?: CanvasAxisProps<X> | null
    right?: CanvasAxisProps<Y> | null
    bottom?: CanvasAxisProps<X> | null
    left?: CanvasAxisProps<Y> | null
    theme: CompleteTheme
}

export const getCanvasAxes = <X extends ScaleValue, Y extends ScaleValue>({
    xScale,
    yScale,
    width,
    height,

    top,
    right,
    bottom,
    left,

    theme,
}: GetCanvasAxesProps<X, Y>) => {
    const axes = { top, right, bottom, left }

    return positions
        .map(position => {
            const axis = axes[position] as typeof position extends 'bottom' | 'top'
                ? CanvasAxisProps<X> | undefined
                : CanvasAxisProps<Y> | undefined

            if (!axis) return null

            const isXAxis = position === 'top' || position === 'bottom'
            const ticksPosition = position === 'top' || position === 'left' ? 'before' : 'after'
            const scale = isXAxis ? xScale : yScale
            const format = getFormatter(axis.format, scale)

            return getCanvasAxis({
                ...axis,
                axis: isXAxis ? 'x' : 'y',
                x: position === 'right' ? width : 0,
                y: position === 'bottom' ? height : 0,
                scale,
                format,
                length: isXAxis ? width : height,
                ticksPosition,
                theme,
            })
        })
        .flat(1)
        .filter((item): item is Exclude<typeof item, null> => !!item)
}

export const useCanvasAxes = <X extends ScaleValue, Y extends ScaleValue>({
    xScale,
    yScale,
    width,
    height,
    top,
    right,
    bottom,
    left,
    theme,
}: GetCanvasAxesProps<X, Y>) => {
    return useMemo(
        () =>
            getCanvasAxes({
                xScale,
                yScale,
                width,
                height,
                top,
                right,
                bottom,
                left,
                theme,
            }),
        [bottom, height, left, right, theme, top, width, xScale, yScale]
    )
}

export const renderAxesToCanvas = <X extends ScaleValue, Y extends ScaleValue>(
    ctx: CanvasRenderingContext2D,
    props: GetCanvasAxesProps<X, Y>
) => {
    getCanvasAxes(props).forEach(item => item.render(ctx))
}
